import dayjs from 'dayjs';
import { ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import _ from 'lodash';

const ListPayment = ({
  bank,
  selectedBank,
  selectedType,
  selectedTransactionType,
}) => {
  const selectedList = bank?.filter((b) => b?._id === selectedBank);

  const formatCurrency = (value) => {
    if (typeof value !== 'number') value = 0;
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const mapTransactionLabel = (type) => {
    switch (type) {
      case 'TRANSFER_IN':
      case 'INCOME':
        return 'เงินเข้า / เงินตั้งต้น';
      case 'GOLD_PURCHASE':
        return 'ซื้อทอง';
      case 'GOLD_SALE':
        return 'ขายทอง';
      case 'EXPENSE':
        return 'เงินออก';
      case 'OTHER':
        return 'อื่นๆ';
      default:
        return type || '—';
    }
  };

  const getArrowIcon = (type) => {
    if (type === 'INCOME' || type === 'TRANSFER_IN') {
      return <ArrowDownCircle className="text-green-600 w-5 h-5" />;
    }
    if (type === 'EXPENSE' || type === 'TRANSFER_OUT') {
      return <ArrowUpCircle className="text-red-600 w-5 h-5" />;
    }
    return <span className="text-gray-400">—</span>;
  };
  const matchType = (type, selectedType) => {
    if (!selectedType) return true; // แสดงทั้งหมด

    switch (selectedType) {
      case 'INCOME':
        return ['INCOME'].includes(type);

      case 'EXPENSE':
        return ['EXPENSE'].includes(type);
      case 'TRANSFER_IN':
        return ['TRANSFER_IN'].includes(type);

      case 'TRANSFER_OUT':
        return ['TRANSFER_OUT'].includes(type);
      default:
        return type === selectedType;
    }
  };
  const matchTransactionType = (transactionType, selectedTransactionType) => {
    if (!selectedTransactionType) return true; // แสดงทั้งหมด

    switch (selectedTransactionType) {
      case 'INCOME':
        return ['INCOME', 'TRANSFER_IN'].includes(transactionType);
      case 'EXPENSE':
        return ['EXPENSE'].includes(transactionType);
      case 'OTHER':
        return ['OTHER'].includes(transactionType);
      case 'GOLD_SALE':
        return ['GOLD_SALE'].includes(transactionType);

      case 'GOLD_PURCHASE':
        return ['GOLD_PURCHASE'].includes(transactionType);
      default:
        return transactionType === selectedTransactionType;
    }
  };
  const filteredTransfer = useMemo(() => {
    if (!bank) return [];

    let list =
      selectedBank === 'all'
        ? _.flatten(bank.map((b) => b.transactions || []))
        : selectedList?.[0]?.transactions || [];

    // ⭐ ใส่ filter ชนิดตาม selectedType
    if (selectedType) {
      list = list.filter((t) => matchType(t.type, selectedType));
    }
    if (selectedTransactionType) {
      list = list.filter((t) =>
        matchTransactionType(t.transactionType, selectedTransactionType),
      );
    }

    // เรียงวันที่ล่าสุดก่อน
    return _.orderBy(list, (t) => new Date(t.date), 'desc');
  }, [selectedBank, bank, selectedList, selectedType]);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden mt-8 border border-gray-200">
      {/* HEADER */}
      <div
        className="px-6 py-5 
    bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100
    border-b border-gray-200 
    rounded-t-xl
    shadow-sm
"
      >
        <h2
          className="text-2xl font-semibold 
      text-[#1A2B4C] 
      tracking-wide
  "
        >
          รายการธุรกรรม (Transactions)
        </h2>

        <p className="text-sm text-gray-500 mt-1">สรุปรายการเดินบัญชีล่าสุด</p>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead
            className="
    bg-gradient-to-r from-gray-50 to-gray-100
    border-b border-gray-200
"
          >
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wide">
                วันที่/เวลา
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wide">
                รายการ
              </th>
              <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700 tracking-wide">
                สถานะ
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 tracking-wide">
                ช่องทาง
              </th>
              <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700 tracking-wide">
                จำนวนเงิน
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-100">
            {filteredTransfer?.map((transfer, index) => (
              <tr
                key={index}
                className="hover:bg-gray-50 transition shadow-sm hover:shadow rounded-lg"
              >
                {/* DATE */}
                <td className="px-6 py-4 text-sm text-gray-800 whitespace-nowrap">
                  <div className="font-medium">
                    {dayjs(transfer?.date).format('DD/MM/YYYY')}
                  </div>
                  <div className="text-xs text-gray-500">
                    เวลา {dayjs(transfer?.date).format('HH:mm')} น.
                  </div>
                </td>

                {/* LABEL */}
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  {mapTransactionLabel(transfer?.transactionType)}
                </td>

                {/* ICON */}
                <td className="px-6  pl-10 py-4 text-center">
                  {getArrowIcon(transfer?.type)}
                </td>

                {/* BANK */}
                <td className="px-6 py-4 text-sm">
                  <div className="font-medium text-gray-800">
                    {transfer?.bankName || '—'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {transfer?.accountNumber}
                  </div>
                </td>

                {/* AMOUNT */}
                <td className="px-6 py-4 text-right whitespace-nowrap">
                  <span
                    className={`px-3 py-1 rounded-lg text-sm font-semibold inline-block ${
                      transfer?.type === 'INCOME' ||
                      transfer?.type === 'TRANSFER_IN'
                        ? 'text-green-700 bg-green-50'
                        : 'text-red-700 bg-red-50'
                    }`}
                  >
                    {formatCurrency(Math.abs(transfer?.amount || 0))}
                  </span>
                </td>
              </tr>
            ))}

            {filteredTransfer.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="text-center py-6 text-gray-500 font-medium"
                >
                  — ไม่พบข้อมูลรายการ —
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ListPayment;
